/**
 * useCardScanPair.js — v228.8.6
 *
 * Centralizes the "wait for BOTH sides before AI auto-fill / payer matching"
 * contract shared by every CardScanner caller (onboarding + profile, insurance
 * + ID). The shared modal works the same everywhere because this hook owns the
 * gating: each side capture only records that side's image + extracted data;
 * the merged analysis (`onComplete`) fires exactly once — after both the front
 * AND back have been captured.
 *
 * Why this matters (payer mapping bug): the medical/EDI claims payer ID
 * (`cardPayerId`) is printed on the BACK of an insurance card. The server's
 * payer resolver PREFERS that printed ID over the ambiguous brand name
 * ("Health Net" → wrong Stedi entry). If the match runs on front-only data the
 * back's payer ID never reaches the resolver, so eligibility logs
 * PAYER_NOT_MAPPED. Deferring the match until the back is in — and letting the
 * back's card-printed payer win over the front's name guess — is the fix.
 *
 * Usage:
 *   const pair = useCardScanPair('insurance', (merged, { frontCard, backCard }) => {
 *     // apply auto-fill + payer match exactly once, both sides present
 *   })
 *   // on each scanner capture (caller still stores the raw image immediately so
 *   // the slot shows "captured"):
 *   pair.captureSide('front' | 'back' | 'cardFront' | 'cardBack' | 'idFront' | 'idBack', cardData)
 *
 * cardType: 'insurance' | 'ID' (case-insensitive)
 *
 * PROD-1189 (2026-10-19): the both-sides wait is an INSURANCE rule. For an ID only the FRONT
 * is read (the back is capture-only, no OCR), so waiting for the back bought nothing and a
 * front-only scan filled nothing at all. For an ID, a front that arrives with extracted
 * fields fires onComplete AT ONCE (front fields only, backCard null), once per distinct
 * `extracted` object: persistCardData re-captures the same side with an enriched copy that
 * carries the SAME extractedData reference, and that must not fire again. A back fires
 * again only when it carries extracted keys of its own. Insurance is unchanged.
 *
 * PROD-1189b (2026-10-28) — STALE RESULTS: an extract/upload result can arrive AFTER the patient
 * removed that card or put another one in its place. Each request takes a token first:
 *   const token = pair.beginSide(side)        // before the extract/upload starts (replaces older ones)
 *   ... await processCardFile(...) ...
 *   if (!pair.isCurrent(token)) return         // cleared or replaced meanwhile: drop it, touch nothing
 *   pair.captureSide(side, cardData, token)    // also refuses a stale token (returns false)
 * clearSide(side) and reset() invalidate every token of that side; a newer beginSide(side) invalidates
 * the older ones. A captureSide without a token behaves as before.
 */
import { useRef, useCallback, useEffect } from 'react'
import { payerNamesAgree } from '../utils/payerNamesAgree'

// PROD-1190: the payer keys a back never fills in a front whose payer verdict it may not replace.
const BACK_HELD_PAYER_KEYS = new Set([
  'payerId', 'payer_id', 'stediPayerId', 'primaryPayerId', 'payerName', 'stediPayerName',
  'payerMatchConfidence', 'payerMatchSource', 'payerNeedsConfirmation', 'payerCandidates',
  'payerEligibility', 'provider', 'cardInsurerName', 'regionCandidates',
])

export default function useCardScanPair(cardType, onComplete) {
  const frontRef = useRef(null) // { cardData, extracted }
  const backRef = useRef(null)
  // PROD-1189: the ID front `extracted` object onComplete last fired for (ID only).
  const firedFrontExtractedRef = useRef(null)
  // PROD-1189b: one generation counter per side; a token is valid only while its number is current.
  const genRef = useRef({ front: 0, back: 0 })
  const onCompleteRef = useRef(onComplete)
  useEffect(() => { onCompleteRef.current = onComplete })

  const isInsurance = String(cardType || '').toLowerCase() === 'insurance'

  const normalize = (side) =>
    (side === 'front' || side === 'cardFront' || side === 'idFront') ? 'front' : 'back'

  const beginSide = useCallback((side) => {
    const norm = normalize(side)
    genRef.current[norm] += 1
    return { side: norm, gen: genRef.current[norm] }
  }, [])
  const isCurrent = useCallback((token) => !!(token && genRef.current[token.side] === token.gen), [])

  const captureSide = useCallback((side, cardData, token) => {
    const norm = normalize(side)
    // PROD-1189b: a result whose side was cleared or replaced since it started is dropped whole.
    if (token !== undefined && (!token || token.side !== norm || genRef.current[norm] !== token.gen)) return false
    const entry = {
      cardData: cardData || null,
      extracted: (cardData && cardData.extractedData) || null,
    }
    if (norm === 'front') frontRef.current = entry
    else backRef.current = entry

    const hasKeys = (o) => !!(o && typeof o === 'object' && Object.keys(o).length)
    if (isInsurance) {
      // Insurance: only proceed with auto-fill + matching once BOTH sides are present
      // (the back's printed payer ID must reach the resolver).
      if (!frontRef.current || !backRef.current) return true
    } else if (norm === 'front') {
      // PROD-1189 — ID: the front is the only side that is read. Fill from it at once,
      // once per distinct extracted object (an enriched re-capture keeps the same one).
      if (!hasKeys(entry.extracted)) return true
      if (firedFrontExtractedRef.current === entry.extracted) return true
      firedFrontExtractedRef.current = entry.extracted
    } else {
      // ID back: fire again only when it carries extracted keys of its own.
      if (!frontRef.current || !hasKeys(entry.extracted)) return true
    }

    const f = (frontRef.current && frontRef.current.extracted) || {}
    const b = (backRef.current && backRef.current.extracted) || {}

    // Field union: front-first for member/demographic fields, fill any gaps
    // from the back. (For ID, the back has no OCR so this is just the front.)
    // ★★ PROD-1190: a back whose printed payer ID is a TRAVEL / away-from-home / other-insurer ID
    //   (orella-api API-Paket 375: `travelPayerId`, or cardPayerIdScope 'secondary') names the
    //   insurer that serves the member AWAY from home, not the card's payer. It never replaces the
    //   front's payer: it does not win below, and when the front has a payer verdict of its own
    //   (a payer id, regions to pick from, or an insurer name) none of the back's payer keys fill
    //   the front's gaps either (a gap filled from the back would mix two payers' ids, or put a
    //   payer id beside the front's regions). An answer without those keys (API 374) is unchanged.
    //   Likewise a front that names a regional insurer and no region (`regionCandidates`) keeps
    //   its regions over a back that only matched a NAME: the patient picks the region. Only a
    //   primary payer ID printed on the back (the win below) settles the payer instead.
    const backIdIsTravel = !!(String(b.travelPayerId || '').trim()
      || String(b.cardPayerIdScope || '').trim().toLowerCase() === 'secondary')
    const frontHasRegions = Array.isArray(f.regionCandidates) && f.regionCandidates.length > 0
    const frontHasPayer = frontHasRegions
      || ['payerId', 'payer_id', 'stediPayerId', 'primaryPayerId', 'provider'].some((k) => String(f[k] || '').trim())
    const skipBackKey = isInsurance && (frontHasRegions || (backIdIsTravel && frontHasPayer)) ? BACK_HELD_PAYER_KEYS : null

    const merged = { ...f }
    for (const k of Object.keys(b)) {
      const v = b[k]
      if (v === undefined || v === null || v === '') continue
      if (skipBackKey && skipBackKey.has(k)) continue
      if (merged[k] === undefined || merged[k] === null || merged[k] === '') merged[k] = v
    }

    // Insurance only: the BACK's card-printed payer ID (cardPayerId-resolved)
    // is unambiguous and WINS over the front's brand-name guess -- unless it is a travel ID.
    if (isInsurance) {
      const backHasPrintedPayer = !backIdIsTravel &&
        b.payerId && (b.payerMatchSource === 'cardPayerId' || b.payerMatchSource === 'cardPayerIdRaw')
      if (backHasPrintedPayer) {
        merged.payerId = b.payerId
        if (b.stediPayerId) merged.stediPayerId = b.stediPayerId
        if (b.primaryPayerId) merged.primaryPayerId = b.primaryPayerId
        if (b.payerName) merged.payerName = b.payerName
        if (b.provider) merged.provider = b.provider
        if (b.payerEligibility) merged.payerEligibility = b.payerEligibility
        merged.payerMatchSource = b.payerMatchSource
        // v242.64.0 (TF-11): the back winning has to be the WHOLE verdict. The
        // field union above only fills gaps, so the FRONT's confidence, its
        // confirm flag and its rejected alternates all survived — the record
        // routed on the back's printed payer ID while the patient was still
        // shown the front's wrong plan name and asked to confirm it.
        merged.payerMatchConfidence = b.payerMatchConfidence || 'high'
        merged.payerNeedsConfirmation = !!b.payerNeedsConfirmation
        merged.stediPayerName = b.stediPayerName || b.payerName || ''
        merged.payerCandidates = b.payerCandidates || []
        // ★★ R23 / TEST 1.01 (patient P1): the printed payer ID settles WHICH payer it names, not
        //   that it is this patient's insurer. orella-api D5b compares it with the names read in
        //   the SAME request; the front was a separate request, so the server never saw the
        //   front's insurer name next to the back's payer ID. Here both are in: when the front
        //   read a name that cannot name the back's payer (utils/payerNamesAgree.js, the server's
        //   own rule), the patient confirms through the existing prompt.
        if (b.payerMatchSource === 'cardPayerId' && !merged.payerNeedsConfirmation
            && !(f.payerId && f.payerId === b.payerId)
            && !payerNamesAgree(f.provider, { displayName: b.payerName, payerName: b.stediPayerName || b.provider })) {
          merged.payerNeedsConfirmation = true
          merged.payerMatchConfidence = 'medium'
        }
      }
    }

    const payload = {
      frontCard: frontRef.current ? frontRef.current.cardData : null,
      backCard: backRef.current ? backRef.current.cardData : null,
    }
    // Defer one microtask so the caller's captured-image state paints before
    // the auto-fill writes land (avoids a flash of empty → filled).
    Promise.resolve().then(() => {
      try { onCompleteRef.current && onCompleteRef.current(merged, payload) }
      catch (e) { /* non-fatal — auto-fill never blocks the flow */ }
    })
    return true
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isInsurance])

  const reset = useCallback(() => {
    frontRef.current = null; backRef.current = null; firedFrontExtractedRef.current = null
    genRef.current.front += 1; genRef.current.back += 1
  }, [])
  // v228.11.3: clear just one side (the patient removed that card) without
  // wiping the other side's capture.
  const clearSide = useCallback((side) => {
    const norm = normalize(side)
    genRef.current[norm] += 1 // PROD-1189b: whatever is still in flight for this side is now stale
    if (norm === 'front') { frontRef.current = null; firedFrontExtractedRef.current = null }
    else backRef.current = null
  }, [])
  const bothCaptured = useCallback(() => !!(frontRef.current && backRef.current), [])

  return { captureSide, beginSide, isCurrent, reset, clearSide, bothCaptured }
}
